import type { EChartsOption } from 'echarts'
import { useMemo, type ReactNode } from 'react'
import { amount, moneyCompact, monthLabel, pct, units } from '../lib/format'
import { axisStyle, baseOption, Chart } from './Chart'
import { cx, DataTable, Panel, Spinner } from './ui'
import { useT } from '../lib/i18n'

export interface HistoryRow {
  month: string
  units: number
  revenueNet: number
  freight: number
  unitCost: number
  unitPrice: number
  marginPerUnit: number
  marginPct: number
}
export interface HistorySummary {
  units: number
  revenue: number
  unitPrice: number | null
  marginPerUnit: number | null
  marginPct: number | null
}
export interface ArticleLine {
  productId: string
  name: string
  units: number
  revenue: number
  unitPrice: number | null
  marginPerUnit: number | null
  marginPct: number | null
  share: number
  discountPct: number | null
}
export interface PairTerms {
  lines: number
  listPrice: number | null
  discountPct: number | null
  orderTypes: Record<string, number>
  contracts: number
}
export interface Benchmark {
  customers: number
  median: number | null
  p25: number | null
  p75: number | null
  min: number | null
  max: number | null
  thisPrice: number | null
  percentile: number | null
}
export interface HistoryResponse {
  basis: string
  currency: string
  month: string
  customer: { window: { from: string; to: string }; rows: HistoryRow[]; summary: HistorySummary; articles: number; lines: ArticleLine[] }
  pair: { window: { from: string; to: string }; rows: HistoryRow[]; summary: HistorySummary; terms: PairTerms | null } | null
  benchmark: Benchmark | null
  /** the article's latest sales to other customers */
  others?: { customerId: string; customerName: string; date: string; byMonth: boolean; units: number; price: number }[]
}

function series(rows: HistoryRow[], window: { from: string; to: string }) {
  const months: string[] = []
  let [y, m] = window.from.split('-').map(Number) as [number, number]
  for (let i = 0; i < 12; i++) {
    months.push(`${y}-${String(m).padStart(2, '0')}`)
    m++
    if (m > 12) {
      m = 1
      y++
    }
  }
  const byMonth = new Map(rows.map((r) => [r.month, r]))
  return {
    months,
    units: months.map((mm) => byMonth.get(mm)?.units ?? 0),
    price: months.map((mm) => byMonth.get(mm)?.unitPrice ?? null),
    cost: months.map((mm) => byMonth.get(mm)?.unitCost ?? null),
  }
}

/** Where this customer's price sits among every customer buying the article: a range bar with the median and this customer marked. */
function BenchmarkStrip({ b, cur }: { b: Benchmark; cur: string }) {
  const { t } = useT()
  const lo = b.min ?? 0
  const hi = b.max ?? 1
  const x = (v: number) => `${hi > lo ? ((v - lo) / (hi - lo)) * 100 : 50}%`
  const mine = b.thisPrice!
  const vsMedian = b.median ? mine / b.median - 1 : null
  return (
    <div className="mt-5 border-t border-slate-800 pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 text-[13px]">
        <span className="text-slate-300">
          {t('Across')} <b className="text-slate-100">{t('{n} customers', { n: b.customers })}</b>, {t('median')} <b className="tabular text-slate-100">{amount(b.median)}</b> {cur}/{t('unit')}
        </span>
        <span className="text-slate-500">
          {t('This customer pays')}{' '}
          <b className={cx('tabular', vsMedian !== null && vsMedian < 0 ? 'text-rose-500' : 'text-emerald-500')}>
            {vsMedian === null ? '–' : `${vsMedian < 0 ? '−' : '+'}${Math.abs(vsMedian * 100).toFixed(1).replace('.', ',')} %`}
          </b>{' '}
          {t('vs median · more than {p} of customers', { p: pct(b.percentile, 0) })}
        </span>
      </div>
      <div className="relative mt-3 h-8">
        <div className="absolute left-0 right-0 top-[13px] h-[6px] rounded-full bg-slate-800" />
        {b.p25 !== null && b.p75 !== null && (
          <div className="absolute top-[13px] h-[6px] rounded-full bg-sky-500/30" style={{ left: x(b.p25), width: `calc(${x(b.p75)} - ${x(b.p25)})` }} />
        )}
        {b.median !== null && <div className="absolute top-[9px] h-[14px] w-[2px] -translate-x-1/2 bg-sky-500" style={{ left: x(b.median) }} />}
        <div className="absolute top-[16px] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-gold shadow" style={{ left: x(mine) }} />
        <div className="absolute top-[24px] text-[10px] tabular text-slate-500" style={{ left: 0 }}>{amount(lo)}</div>
        <div className="absolute right-0 top-[24px] text-[10px] tabular text-slate-500">{amount(hi)}</div>
      </div>
    </div>
  )
}

/** The article's latest sales to other customers: who, when, how much, at what price. */
function OtherSales({ rows, cur }: { rows: NonNullable<HistoryResponse['others']>; cur: string }) {
  const { t, lang } = useT()
  const when = (r: (typeof rows)[number]) =>
    r.byMonth ? monthLabel(r.date) : new Date(`${r.date}T00:00:00`).toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
  return (
    <div className="mt-5 border-t border-slate-800 pt-4">
      <div className="label mb-2">{t('Latest sales to other customers')}</div>
      <table className="w-full text-[13px]">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i ? 'border-t border-slate-800' : ''}>
              <td className="py-1.5 pr-3 text-slate-200">{r.customerName}</td>
              <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">{when(r)}</td>
              <td className="py-1.5 pr-3 text-right tabular text-slate-400">{units(r.units)}</td>
              <td className="py-1.5 whitespace-nowrap text-right tabular">
                <b className="text-slate-100">{amount(r.price)}</b> <span className="text-slate-500">{cur}/{t('unit')}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A row of figures: the number, the label beneath it. No boxes. */
function Stats({ items }: { items: { label: string; value: ReactNode; note?: string; tone?: 'neg' | undefined }[] }) {
  return (
    <div className="mb-6 flex flex-wrap gap-x-12 gap-y-4">
      {items.map((s, i) => (
        <div key={i} className="min-w-0">
          <div className={cx('text-[24px] font-extrabold leading-none tracking-tight tabular', s.tone === 'neg' ? 'text-rose-500' : 'text-sky-500')}>
            {s.value}
            {s.note && <span className="ml-1.5 text-[12px] font-normal text-slate-500">{s.note}</span>}
          </div>
          <div className="label mt-1.5 whitespace-nowrap">{s.label}</div>
        </div>
      ))}
    </div>
  )
}

const common = (c: Parameters<typeof baseOption>[0], months: string[]) => ({
  ...baseOption(c),
  grid: { left: 4, right: 4, top: 30, bottom: 4, containLabel: true },
  legend: { top: 0, left: 0, textStyle: { color: c.text, fontSize: 11 }, itemWidth: 18, itemHeight: 8, itemGap: 16 },
  xAxis: { type: 'category' as const, data: months.map(monthLabel), ...axisStyle(c), splitLine: { show: false }, axisLabel: { color: c.text, fontSize: 11, interval: 1 } },
})

export function HistoryPanel({ data, target, loading, productId }: { data: HistoryResponse | null; target?: number; loading?: boolean; productId?: string | undefined }) {
  const { t, lang } = useT()
  const pairS = useMemo(() => (data?.pair ? series(data.pair.rows, data.pair.window) : null), [data])

  const buildPair = useMemo(
    () => (c: Parameters<typeof baseOption>[0]): EChartsOption => ({
      ...common(c, pairS?.months ?? []),
      yAxis: [
        { type: 'value' as const, ...axisStyle(c), axisLabel: { color: c.text, fontSize: 11 }, splitLine: { lineStyle: { color: c.grid, type: 'dashed' as const } } },
        { type: 'value' as const, ...axisStyle(c), splitLine: { show: false }, axisLabel: { color: c.text, fontSize: 11 }, min: (v: { min: number }) => Math.floor(v.min * 0.95), max: (v: { max: number }) => Math.ceil(v.max * 1.03) },
      ],
      series: [
        { name: t('Units'), type: 'bar' as const, data: pairS?.units ?? [], itemStyle: { color: c.grid, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 18 },
        { name: t('Realised €/unit'), type: 'line' as const, yAxisIndex: 1, data: pairS?.price ?? [], itemStyle: { color: c.accent }, lineStyle: { color: c.accent, width: 2 }, symbol: 'circle', symbolSize: 6, connectNulls: false },
        { name: t('Unit cost'), type: 'line' as const, yAxisIndex: 1, data: pairS?.cost ?? [], itemStyle: { color: c.gold }, lineStyle: { color: c.gold, width: 1.5, type: 'dashed' as const }, symbol: 'none', showSymbol: false },
        ...(target
          ? [{ name: t('Target now'), type: 'line' as const, yAxisIndex: 1, data: pairS?.months.map(() => target) ?? [], itemStyle: { color: c.pos }, lineStyle: { color: c.pos, width: 1.5, type: 'dotted' as const }, symbol: 'none', silent: true }]
          : []),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairS, target, lang],
  )

  if (loading) return <Spinner label={t('Loading history…')} />
  if (!data) return null
  const cur = data.currency
  const p = data.pair
  const window = `${monthLabel(data.customer.window.from)} – ${monthLabel(data.customer.window.to)}`
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Panel title={t('This article at this customer')} right={<span className="text-[12px] text-slate-500">{window}</span>}>
        {p && p.rows.length > 0 ? (
          <>
            <Stats
              items={[
                { label: t('Units'), value: units(p.summary.units) },
                { label: t('Price per unit'), value: amount(p.summary.unitPrice) },
                ...(p.terms?.discountPct !== null && p.terms?.discountPct !== undefined
                  ? [{ label: t('Off list'), value: pct(p.terms.discountPct), note: `${t('list')} ${amount(p.terms.listPrice)}` }]
                  : []),
                { label: t('Margin per unit'), value: amount(p.summary.marginPerUnit), tone: (p.summary.marginPerUnit ?? 0) < 0 ? 'neg' : undefined },
                { label: t('Margin'), value: pct(p.summary.marginPct) },
              ]}
            />
            <Chart build={buildPair} height={230} />
            {data.benchmark && data.benchmark.customers > 1 && data.benchmark.thisPrice !== null && (
              <BenchmarkStrip b={data.benchmark} cur={cur} />
            )}
          </>
        ) : (
          <p className="py-10 text-center text-[13px] text-slate-500">{t('No history for this article at this customer.')}</p>
        )}
        {data.others && data.others.length > 0 && <OtherSales rows={data.others} cur={cur} />}
      </Panel>
      <Panel title={t('This customer, all articles')} right={<span className="text-[12px] text-slate-500">{t('{n} article(s)', { n: data.customer.articles })} · {window}</span>}>
        {data.customer.rows.length > 0 ? (
          <>
            <Stats
              items={[
                { label: t('Revenue'), value: moneyCompact(data.customer.summary.revenue, cur) },
                { label: t('Margin'), value: pct(data.customer.summary.marginPct) },
              ]}
            />
            <DataTable
              rows={data.customer.lines}
              rowKey={(l) => l.productId}
              maxHeight="20rem"
              columns={[
                { key: 'a', header: t('Article'), className: 'whitespace-normal', render: (l) => <span className={cx(l.productId === productId ? 'font-semibold text-sky-500' : 'text-slate-200')}>{l.name}</span> },
                { key: 'u', header: t('Units'), align: 'right', render: (l) => units(l.units) },
                { key: 'p', header: `${t('Price')} ${cur}/${t('unit')}`, align: 'right', render: (l) => amount(l.unitPrice) },
                { key: 'm', header: t('Margin/unit'), align: 'right', render: (l) => <span className={(l.marginPerUnit ?? 0) < 0 ? 'text-rose-500' : ''}>{amount(l.marginPerUnit)}</span> },
                { key: 'mp', header: t('Margin'), align: 'right', render: (l) => pct(l.marginPct) },
                ...(data.customer.lines.some((l) => l.discountPct !== null) ? [{ key: 'd', header: t('Off list'), align: 'right' as const, render: (l: ArticleLine) => (l.discountPct === null ? '–' : pct(l.discountPct)) }] : []),
                { key: 's', header: t('Share'), align: 'right', render: (l) => pct(l.share, 0) },
              ]}
            />
          </>
        ) : (
          <p className="py-10 text-center text-[13px] text-slate-500">{t('No history for this customer.')}</p>
        )}
      </Panel>
    </div>
  )
}
